import { Show, Suspense, createSignal, For, Switch, Match } from "solid-js";
import { useParams, A, createAsync, revalidate, useAction, type RouteDefinition } from "@solidjs/router";
import { Title } from "@solidjs/meta";
import { AppShell } from "~/components/layout";
import {
  Button, Badge, Card, CardContent, CardHeader, CardTitle, Skeleton,
  Textarea, Input, Separator, Breadcrumbs
} from "~/components/ui";
import { getPrediction, getPredictions, updatePredictionStatus, addPredictionEvidence, updatePredictionNotes } from "~/server/bff";
import { formatDate, formatDuration, formatRelativeTime } from "~/lib/utils";
import {
  ArrowLeft, Calendar, Clock, CheckCircle, XCircle, Play,
  ExternalLink, Plus, Link, Save, Target, Loader2
} from "lucide-solid";

export const route = {
  preload: ({ params }) => getPrediction(params.id as string),
} satisfies RouteDefinition;

export default function PredictionDetailPage() {
  const params = useParams();
  const [notes, setNotes] = createSignal("");
  const [newEvidenceLink, setNewEvidenceLink] = createSignal("");
  const [isUpdatingStatus, setIsUpdatingStatus] = createSignal(false);
  const [isAddingEvidence, setIsAddingEvidence] = createSignal(false);
  const [isSavingNotes, setIsSavingNotes] = createSignal(false);

  const updateStatusAction = useAction(updatePredictionStatus);
  const addEvidenceAction = useAction(addPredictionEvidence);
  const updateNotesAction = useAction(updatePredictionNotes);

  const prediction = createAsync(() => getPrediction(params.id || ""), { deferStream: true });
  const predictionId = () => params.id || "";

  const handleUpdateStatus = async (status: "verified" | "failed") => {
    setIsUpdatingStatus(true);
    const formData = new FormData();
    formData.append("id", predictionId());
    formData.append("status", status);
    await updateStatusAction(formData);
    await revalidate([getPrediction.keyFor(predictionId()), getPredictions.key]);
    setIsUpdatingStatus(false);
  };

  const handleAddEvidence = async () => {
    const link = newEvidenceLink().trim();
    if (!link) return;
    setIsAddingEvidence(true);
    const formData = new FormData();
    formData.append("id", predictionId());
    formData.append("link", link);
    await addEvidenceAction(formData);
    await revalidate([getPrediction.keyFor(predictionId()), getPredictions.key]);
    setNewEvidenceLink("");
    setIsAddingEvidence(false);
  };

  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    const formData = new FormData();
    formData.append("id", predictionId());
    formData.append("notes", notes());
    await updateNotesAction(formData);
    await revalidate([getPrediction.keyFor(predictionId()), getPredictions.key]);
    setIsSavingNotes(false);
  };

  return (
    <AppShell>
      <Title>{prediction()?.event ?? "Prediction"} - IntelAnalyst</Title>
      <div class="p-6 max-w-4xl mx-auto">
        {/* Breadcrumbs */}
        <Breadcrumbs
          items={[
            { label: "Track", href: "/track/predictions" },
            { label: "Predictions", href: "/track/predictions" },
            { label: prediction()?.event ?? "Loading..." },
          ]}
          showHome
          class="mb-6"
        />

        <Suspense fallback={<PredictionSkeleton />}>
          <Show when={prediction()}>
            {(p) => (
              <div class="space-y-6">
                <div>
                  <div class="flex items-start justify-between gap-4 mb-4">
                    <h1 class="text-2xl font-bold leading-tight">{p().event}</h1>
                    <StatusBadgeLarge status={p().status} />
                  </div>

                  <Card class="bg-muted/30">
                    <CardContent class="p-4">
                      <p class="text-lg italic">"{p().originalQuote}"</p>
                      <div class="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                        <A
                          href={`/explore/sources/${p().channelId}`}
                          class="font-medium hover:text-foreground"
                        >
                          {p().channel?.name}
                        </A>
                        <A
                          href={`/video/${p().videoId}?t=${p().timestamp}`}
                          class="flex items-center gap-1 hover:text-foreground font-mono"
                        >
                          <Play class="h-3 w-3" />
                          @ {formatDuration(p().timestamp)}
                        </A>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div class="grid gap-4 md:grid-cols-3">
                  <Card>
                    <CardContent class="p-4">
                      <p class="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                        Target Date
                      </p>
                      <p class="text-lg font-medium flex items-center gap-2">
                        <Calendar class="h-4 w-4" />
                        {p().targetDate ? formatDate(p().targetDate!) : "Not specified"}
                      </p>
                      <Show when={p().timeframe}>
                        <p class="text-sm text-muted-foreground mt-1">{p().timeframe}</p>
                      </Show>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent class="p-4">
                      <p class="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                        Confidence
                      </p>
                      <p class="text-lg font-medium">
                        {p().confidence || "Not specified"}
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent class="p-4">
                      <p class="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                        Created
                      </p>
                      <p class="text-lg font-medium">
                        {formatRelativeTime(p().createdAt)}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle class="text-lg">Update Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div class="flex gap-2">
                      <Button
                        variant={p().status === "verified" ? "default" : "outline"}
                        class="flex-1"
                        disabled={isUpdatingStatus() || p().status === "verified"}
                        onClick={() => handleUpdateStatus("verified")}
                      >
                        <Show when={isUpdatingStatus()} fallback={<CheckCircle class="h-4 w-4 mr-2" />}>
                          <Loader2 class="h-4 w-4 mr-2 animate-spin" />
                        </Show>
                        Mark Verified
                      </Button>
                      <Button
                        variant={p().status === "failed" ? "destructive" : "outline"}
                        class="flex-1"
                        disabled={isUpdatingStatus() || p().status === "failed"}
                        onClick={() => handleUpdateStatus("failed")}
                      >
                        <Show when={isUpdatingStatus()} fallback={<XCircle class="h-4 w-4 mr-2" />}>
                          <Loader2 class="h-4 w-4 mr-2 animate-spin" />
                        </Show>
                        Mark Failed
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle class="text-lg flex items-center gap-2">
                      <Link class="h-4 w-4" />
                      Evidence Links
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Show
                      when={p().evidenceLinks.length > 0}
                      fallback={
                        <p class="text-sm text-muted-foreground mb-4">
                          No evidence links added yet
                        </p>
                      }
                    >
                      <div class="space-y-2 mb-4">
                        <For each={p().evidenceLinks}>
                          {(link) => (
                            <div class="flex items-center gap-2 p-2 rounded bg-muted/50">
                              <ExternalLink class="h-4 w-4 text-muted-foreground" />
                              <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                class="flex-1 text-sm text-primary hover:underline truncate"
                              >
                                {link}
                              </a>
                            </div>
                          )}
                        </For>
                      </div>
                    </Show>

                    <div class="flex gap-2">
                      <Input
                        placeholder="Add evidence URL..."
                        value={newEvidenceLink()}
                        onInput={(e) => setNewEvidenceLink(e.currentTarget.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAddEvidence()}
                        class="flex-1"
                        disabled={isAddingEvidence()}
                      />
                      <Button
                        variant="outline"
                        onClick={handleAddEvidence}
                        disabled={isAddingEvidence() || !newEvidenceLink().trim()}
                        aria-label="Add evidence link"
                        title="Add evidence link"
                      >
                        <Show when={isAddingEvidence()} fallback={<Plus class="h-4 w-4" />}>
                          <Loader2 class="h-4 w-4 animate-spin" />
                        </Show>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle class="text-lg">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      placeholder="Add notes about this prediction..."
                      class="min-h-[100px]"
                      value={notes()}
                      onInput={(e) => setNotes(e.currentTarget.value)}
                      disabled={isSavingNotes()}
                    />
                    <div class="flex justify-end mt-3">
                      <Button size="sm" onClick={handleSaveNotes} disabled={isSavingNotes()}>
                        <Show when={isSavingNotes()} fallback={<Save class="h-4 w-4 mr-1" />}>
                          <Loader2 class="h-4 w-4 mr-1 animate-spin" />
                        </Show>
                        Save Notes
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Show when={p().topicTags.length > 0}>
                  <div>
                    <p class="text-sm font-medium mb-2">Related Topics</p>
                    <div class="flex gap-2">
                      <For each={p().topicTags}>
                        {(tag) => (
                          <A href={`/explore/topics?q=${encodeURIComponent(tag)}`}>
                            <Badge variant="secondary" class="font-mono">
                              {tag}
                            </Badge>
                          </A>
                        )}
                      </For>
                    </div>
                  </div>
                </Show>
              </div>
            )}
          </Show>
        </Suspense>
      </div>
    </AppShell>
  );
}

function StatusBadgeLarge(props: { status: string }) {
  return (
    <Switch fallback={<Badge variant="outline" class="text-base px-3 py-1"><Clock class="h-4 w-4 mr-1" />Pending</Badge>}>
      <Match when={props.status === "verified"}>
        <Badge variant="success" class="text-base px-3 py-1"><CheckCircle class="h-4 w-4 mr-1" />Verified</Badge>
      </Match>
      <Match when={props.status === "failed"}>
        <Badge variant="destructive" class="text-base px-3 py-1"><XCircle class="h-4 w-4 mr-1" />Failed</Badge>
      </Match>
      <Match when={props.status === "due_soon"}>
        <Badge variant="warning" class="text-base px-3 py-1"><Clock class="h-4 w-4 mr-1" />Due Soon</Badge>
      </Match>
      <Match when={props.status === "overdue"}>
        <Badge variant="destructive" class="text-base px-3 py-1"><Clock class="h-4 w-4 mr-1" />Overdue</Badge>
      </Match>
    </Switch>
  );
}

function PredictionSkeleton() {
  return (
    <div class="space-y-6">
      <Skeleton class="h-10 w-3/4" />
      <Skeleton class="h-24 w-full" />
      <div class="grid gap-4 md:grid-cols-3">
        <Skeleton class="h-20" />
        <Skeleton class="h-20" />
        <Skeleton class="h-20" />
      </div>
      <Skeleton class="h-32 w-full" />
    </div>
  );
}
